import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common'
import { ActivatedRoute, Router } from '@angular/router';
import { Sequences } from '@shared/models/activity-summary/sequences';
import { DutyPeriodSummaries } from '@shared/models/activity-summary/duty-period-summaries';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { ButtonModule } from 'primeng/button';
import { DataBoxComponent } from '@shared/components/data-box/data-box.component';
import {
  Map,
  LatLngExpression,
  LatLng,
} from 'leaflet'
import * as lfGeo from 'leaflet.geodesic';
import { DataItem } from '@shared/models/data-item';
import { SequenceTotalsInfoComponent } from './sequence-total-info/sequence-total-info.component'
import { formatTime, formatTimeString, timeStringToDuration } from '@shared/utilities/utilities';
import { FlightItemComponent } from 'app/features/flights/flight-item/flight-item.component';
import { LogbookLeg } from '@shared/models/activity-summary/logbook-leg';
import { MapService } from '../services/map.service';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { AppService } from 'app/app.service';
import { AppSettings } from '@shared/models/settings/app-settings';
import { combineLatest, EMPTY, map, Observable, of, Subject, Subscription, switchMap, takeUntil } from 'rxjs';
import { ActivityRecordService } from '@shared/services/activity-record/activity-record.service';
import { SignalsService } from '@shared/services/signals/signals.service';
import { DateTime } from 'luxon';
import { BreadcrumbService } from '@shared/components/breadcrumb/breadcrumb.service';
import { ADD_CODES } from '@shared/utilities/add-codes';
import { DutyInfoDialogComponent } from './duty-info-dialog/duty-info-dialog.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FlightsService } from '../services/flight.service';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { getContractualMonth } from '@shared/utilities/contract-month/contractualMonth';
import { ErrorLogService } from '@shared/services/errorhandler/error-log.service';
import { RequestState, RequestStatus } from '@shared/models/request-state';
import { s } from '@fullcalendar/core/internal-common';


interface LayoverData {
  station: string;
  length: string;
}

@Component({
  selector: 'app-sequence-detail',
  imports: [
    CommonModule,
    LeafletModule,
    ButtonModule,
    DataBoxComponent,
    FlightItemComponent,
    BreadcrumbComponent,
    PopoverModule,
    DialogModule
  ],
  templateUrl: './sequence-detail.component.html',
  styleUrl: './sequence-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService]
})

export class SequenceDetailComponent implements OnInit, OnDestroy {

  constructor(
    public appService: AppService,
    private router: Router,
    private route: ActivatedRoute,
    private mapService: MapService,
    private cdr: ChangeDetectorRef,
    private dialogService: DialogService,
    private flightService: FlightsService,
    public signalsService: SignalsService,
    private activityRecordService: ActivityRecordService,
    private breadcrumbService: BreadcrumbService,
    private errorLogService: ErrorLogService,
  ) { }

  sequence$?: Observable<RequestState<Sequences>>;

  reasonCodes = ADD_CODES;
  private map?: Map;
  private coords?: LatLngExpression[][];
  private geoLine?: lfGeo.GeodesicLine;

  scheduledTime = '';
  actualTiime = '';
  deadheadTime = '';
  totalTime = '';
  isTimeFormatUTC = false;
  sequenceTotalsInfoDialog = SequenceTotalsInfoComponent;
  ref: DynamicDialogRef | undefined;
  sequenceTotalsDatabox: DataItem[] = [];
  tableListType = 'sequence';
  appSettings?: AppSettings;
  lastUpdated = '';
  addReasonCode = '';
  flagged = false;
  theme$?: string;
  subSettings?: Subscription;

  sequenceNumber = 0;
  originationDate = '';
  destroy$ = new Subject<void>();

  ngOnInit(): void {
    //update breadcrumb service
    const url = location.pathname;
    const breadcrumbLabel = this.route.snapshot.data['breadcrumb'] || 'Sequence';
    this.breadcrumbService.addToBreadcrumbs(url, breadcrumbLabel);
    this.breadcrumbService.removeBreadcrumb(['Flight']);

    this.subSettings = this.appService.theme$.subscribe((theme) => {
      this.theme$ = theme;
      this.cdr.markForCheck();
    });

    const settings = JSON.parse(localStorage.getItem('appSettings') || '');
    if (settings) {
      this.appSettings = settings;
      this.isTimeFormatUTC = settings.dateTimeFormatDefault === 'utc' ? true : false;
    }

    
    /**Full Sequence loaded from Search-Results component */
    const unfilteredSequence = sessionStorage.getItem('unfilteredSequence');
    if (unfilteredSequence) {
      const sequence = JSON.parse(unfilteredSequence);
      const sortedSequences = this.sortList(sequence);
      this.sequence$ = of(
        new RequestState<Sequences>(
        RequestStatus.IsCompleteWithResponse,
        sortedSequences
      ));
      sessionStorage.removeItem('unfilteredSequence'
      )
      this.loadDataBox(sequence);
      this.renderMap(sequence);
    }

    /** Sequence loaded from Flight Table list or calendar */
    else {
      this.errorLogService.logError(`[INFO] - ${DateTime.now().toString()}: Location url - ${location.href}`);
      this.sequence$ = this.route.paramMap.pipe(
        switchMap((params) => {
          this.sequenceNumber = Number(params.get('number'));
          const originationDateParam = params.get('originationDate') || '';
          this.originationDate = DateTime.fromISO(originationDateParam, { zone: 'utc' }).toFormat('yyyy-MM-dd');
          const sequenceYear = this.getContractualBidMonth(this.originationDate).year;
          const sequenceMonth = this.getContractualBidMonth(this.originationDate).month;
          
          //Check if sequence is flagged
          let flaggedSequences: {sequenceNumber: number, originationDate: string}[] = JSON.parse(localStorage.getItem('flaggedSequences') || '[]');
          if (flaggedSequences) {
            const isFlagged = flaggedSequences.find(seq => 
            seq.sequenceNumber === this.sequenceNumber && seq.originationDate === this.originationDate);
            if (isFlagged) {
              this.flagged = true;
            }
          }

          return this.fetchSequence(
            this.sequenceNumber,
            sequenceYear,
            sequenceMonth,
            this.originationDate
          ).pipe(
            map((sequence) => {
              //If sequence is null, render empty map
              if (!sequence || !sequence.response) {
                this.renderMap();
                return sequence.into<Sequences>();
              }
              //if sequence found, render map with coords
              this.renderMap(sequence.response);
              return new RequestState<Sequences>(
                RequestStatus.IsCompleteWithResponse,
                sequence.response
              )
            })
          );
        }),
      )
    }
  }

  fetchSequence(sequenceNumber: number, year: number, month: number, orgDate: string): Observable<RequestState<Sequences>> {
    return this.signalsService.employeeNumber$.pipe(
      switchMap(employeeNumber => {
        if (!employeeNumber) return EMPTY;

        return combineLatest([
          this.activityRecordService.getSummaryRecords(employeeNumber, year, month),
          this.flightService.settingsChangedAction$
        ]).pipe(
          map(([data, appSettings]) => {
            this.appSettings = appSettings;
            let sequence;

            if (!data || !data.response) {
              return data.into<Sequences>();
            }

            sequence = data.response?.sequences.find(seq => {
              const seqOriginationDate = DateTime.fromISO(
                seq.originationDate, { zone: 'utc' }).toFormat('yyyy-MM-dd');

              return seq.sequenceNumber === sequenceNumber
                && seqOriginationDate === orgDate;
            });

            this.addReasonCode = sequence?.addReasonCode || '';
            return new RequestState<Sequences>(
              RequestStatus.IsCompleteWithResponse,
              sequence
            );
          })
        )
      })
    )
  }

  sortList(sequence: Sequences): Sequences {
    sequence;
    if (this.appSettings?.sortOrder === 1) {
      sequence.dutyPeriodSummaries = sequence.dutyPeriodSummaries.reverse();
    }
    return sequence;
  }

  ngOnDestroy(): void {
    this.subSettings?.unsubscribe();
  }

  loadDataBox(sequence: Sequences): any {
    return [
      {
        label: 'Flight',
        value: this.flightTotalizerFor('flight', sequence)
      },
      {
        label: 'Deadhead',
        value: this.flightTotalizerFor('deadhead', sequence)
      },
      {
        label: 'Total',
        value: this.flightTotalizerFor('total', sequence)
      },
      {
        label: 'TAFB',
        value: timeStringToDuration(String(sequence.duration)).toFormat('hh:mm')
      },
    ];
  }

  onResetMap(): void {
    this.mapService.initMap('seqMap');
  }

  /**
   * Calculates and formats the total flight time for a given type of time ("deadhead", "flight", or "total")
   * within a sequence of duty period summaries.
   *
   * @param _timeFor - The type of time to calculate. 
   *                   Acceptable values are:
   *                   - `'deadhead'`: Includes only deadhead legs that are not cancelled.
   *                   - `'flight'`: Includes only non-deadhead legs that are not cancelled.
   *                   - `'total'`: Includes all legs that are not cancelled.
   * @param sequence - The sequence object containing duty period summaries and their respective legs.
   * @returns A formatted string representing the total flight time for the specified type of time.
   */
  flightTotalizerFor(_timeFor: string, sequence: Sequences): string {
    let time = 0;

    switch (_timeFor) {
      case 'deadhead':
        sequence.dutyPeriodSummaries.forEach((duty) => {
          duty.legs.forEach((leg) => {
            if (leg && leg.isDeadhead && !leg.isCancelled) {
              time += +leg.flightTimeActual;
            }
          })
        })
        return formatTime(time);
      case 'flight':
        sequence.dutyPeriodSummaries.forEach((duty) => {
          duty.legs.forEach((leg) => {
            if (leg && !leg.isDeadhead && !leg.isCancelled) {
              time += +leg.flightTimeActual;
            }
          })
        })
        return formatTime(time);

      case 'total':
        sequence.dutyPeriodSummaries.forEach((duty) => {
          duty.legs.forEach((leg) => {
            if (leg && !leg.isCancelled) {
              time += +leg.flightTimeActual;
            }
          })
        })
        return formatTime(time);

      default:
        return ''
    }
  }

  getMapCoords(dutyPeriods: DutyPeriodSummaries[]): LatLngExpression[][] {
    let coords: LatLngExpression[][] = [];
    for (let duty of dutyPeriods) {
      duty.legs.forEach((leg) => {
        if (leg) {
          const stationLatlng = [
            new LatLng(leg.departureStation.latitude, leg.departureStation.longitude),
            new LatLng(leg.arrivalStation.latitude, leg.arrivalStation.longitude)
          ];
          coords.push(stationLatlng);
        }
      })
    }
    return coords
  }



  renderMap(sequence?: Sequences): void {
    let coords: LatLngExpression[][] = [];
    if (sequence) {
      coords = this.getMapCoords(
        sequence.dutyPeriodSummaries || []
      );
    }
    else {
      const emptyCoords = [
        new LatLng(0, 0),
        new LatLng(0, 0)
      ];
      coords.push(emptyCoords);
    }
    this.mapService.renderMap('seqMap', [], coords);
  }

  validateReasonCode(reasonCode: string): string {
    const reasonCodeTrimmed = reasonCode.trim();
    if (reasonCodeTrimmed === '') {
      return '--';
    }
    return reasonCodeTrimmed;
  }

  dutyBoxItems(duty: DutyPeriodSummaries): DataItem[] {
    return [
      {
        label: "Duty Start",
        value: this.formatDutyTime(duty, 'start')
      },
      {
        label: "Duty End",
        value: this.formatDutyTime(duty, 'end')
      },
      {
        label: "On Duty",
        value: formatTime(duty.actualOnDuty)
      },
      {
        label: "FDP",
        value: formatTime(duty.flightDutyPeriod)
      }
    ]
  }

  formatDutyTime(duty: DutyPeriodSummaries, frame: string): string {
    if (this.appSettings?.dateTimeFormatDefault === 'utc') {
      return frame === 'start' ?
        formatTimeString(duty.startTime.utc) + 'Z' :
        formatTimeString(duty.endTime.utc) + 'Z';
    }
    return frame === 'start' ?
      formatTimeString(duty.startTime.local) + 'L' :
      formatTimeString(duty.endTime.local) + 'L';
  }

  getLayover(duty: DutyPeriodSummaries): LayoverData | undefined {
    if (duty.legs.length > 0) {
      for (let leg of duty.legs) {
        if (leg.layover && leg.layover.length && leg.layover.station) {
          const layoverLength = leg.layover.length > 0 ? formatTime(leg.layover.length) : 'err';
          const convertedLayover = {
            station: leg.layover.station,
            length: layoverLength
          }
          return convertedLayover;
        }
      }
    }
    return undefined;
  }

  onDutyHelpClicked(): void {
    this.ref = this.dialogService.open(DutyInfoDialogComponent, {
      header: 'Duty Summary',
      width: '50vw',
      modal: true,
      dismissableMask: true,
      closable: true,
      breakpoints: {
        '500px': '98vw',
      },
      contentStyle: {
        'background-color': 'var(--surface-0)',
      },
    });
  }


  onLegClicked(selectedLeg: LogbookLeg, sequence: Sequences): void {

    const flightDate = this.isTimeFormatUTC ?
      selectedLeg.departure.scheduled.utc :
      selectedLeg.departure.scheduled.local;

    const formattedFltDate = DateTime.fromISO(flightDate).toFormat('yyyy-MM-dd');
    const originationDate = DateTime.fromISO(
      sequence.originationDate, { zone: 'utc' }
    ).toFormat('yyyy-MM-dd');
    const sequenceYear = this.getContractualBidMonth(originationDate).year;
    const sequenceMonth = this.getContractualBidMonth(originationDate).month;
    this.router.navigate([
      'sequence',
      sequence.sequenceNumber,
      sequenceYear,
      sequenceMonth,
      originationDate,
      'flight',
      selectedLeg.flightNumber,
      selectedLeg.departureStation.code,
      selectedLeg.arrivalStation.code,
      formattedFltDate
    ]);
  }

  getContractualBidMonth(originationDate: string): DateTime {
    const year = DateTime.fromISO(originationDate, { zone: 'utc' }).year; // 2025
    const day = DateTime.fromISO(originationDate, { zone: 'utc' }).day; // 1
    const contractualMonth = getContractualMonth({ originationDate: originationDate });

    return DateTime.fromObject({ year: year, month: +contractualMonth.asInt, day: day }, { zone: 'utc' });
  }

  flagSequence(): void {
    this.flagged = !this.flagged;
    if (this.flagged) {
      console.log('Sequence flagged');
      let flaggedSequences: {sequenceNumber: number, originationDate: string}[] = JSON.parse(localStorage.getItem('flaggedSequences') || '[]');
      if (flaggedSequences) {
        const isAlreadyFlagged = flaggedSequences.find(seq => 
          seq.sequenceNumber === this.sequenceNumber && seq.originationDate === this.originationDate);

        if (!isAlreadyFlagged) {
          flaggedSequences.push({sequenceNumber: this.sequenceNumber, originationDate: this.originationDate});
          localStorage.setItem('flaggedSequences', JSON.stringify(flaggedSequences));
        }
      }
    }

    if (!this.flagged) {
      console.log('Sequence unflagged');
      let flaggedSequences: {sequenceNumber: number, originationDate: string}[] = JSON.parse(localStorage.getItem('flaggedSequences') || '[]');
      if (flaggedSequences) {
        flaggedSequences = flaggedSequences.filter(seq => 
          !(seq.sequenceNumber === this.sequenceNumber && seq.originationDate === this.originationDate)
        );
        localStorage.setItem('flaggedSequences', JSON.stringify(flaggedSequences));
      }
    }
  }

  flagButtonStyle = {
    colorScheme: {
      light: {
        root: {
          hoverBackground: 'transparent'
        },
        text: {
          primary: {
            hoverBackground: 'transparent'
          }
        }
      },
      dark: {
        root: {
          hoverBackground: 'transparent'
        },
        text: {
          primary: {
            hoverBackground: 'transparent'
          }
        }
      }
    }
  };
}
